import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, Download, Eye, AlertTriangle } from "lucide-react";
import { API_BASE } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";

const ORDER = [
  "executive_summary", "facts", "legal_issues", "applicable_cyprus_law",
  "case_law", "analysis", "counterarguments", "risks", "next_steps",
  "sources", "disclaimer",
];

function renderSection(value) {
  if (value == null || value === "") return <div className="text-slate-400 text-sm">—</div>;
  if (typeof value === "string") return <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{value}</p>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <div className="text-slate-400 text-sm">—</div>;
    if (typeof value[0] === "string") return (
      <ul className="space-y-2 list-disc ml-5 text-slate-700 leading-relaxed">{value.map((v, i) => <li key={i}>{v}</li>)}</ul>
    );
    return (
      <div className="space-y-3">
        {value.map((v, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            {Object.entries(v).map(([k, val]) => (
              <div key={k} className="text-sm flex gap-2">
                <span className="uppercase text-[10px] tracking-[0.18em] font-bold text-[#8B1E3F] min-w-[80px]">{k.replace(/_/g, " ")}</span>
                <span className="text-slate-700 flex-1">{String(val)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-xs bg-slate-50 p-3 rounded">{JSON.stringify(value, null, 2)}</pre>;
}

export default function PublicReportPage() {
  const { token } = useParams();
  const { t, lang } = useI18n();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(ORDER[0]);

  useEffect(() => {
    fetch(`${API_BASE}/public/reports/${token}`)
      .then((r) => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then(setReport)
      .catch(() => setError(true));
  }, [token]);

  useEffect(() => {
    if (!report) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          const id = visible[0].target.id.replace(/^public-sec-/, "");
          if (id) setActiveSection(id);
        }
      },
      { rootMargin: "-90px 0px -60% 0px", threshold: [0, 0.15, 1] },
    );
    ORDER.forEach((k) => {
      const el = document.getElementById(`public-sec-${k}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [report]);

  if (error) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6">
        <div className="card-premium p-10 text-center max-w-md" data-testid="public-not-found">
          <div className="kicker">Dikigoros.io</div>
          <h1 className="mt-3 font-display font-bold text-3xl text-slate-900">
            {lang === "el" ? "Δεν είναι διαθέσιμο" : "Not available"}
          </h1>
          <p className="mt-3 text-slate-600">
            {lang === "el"
              ? "Αυτή η αναφορά δεν είναι πλέον δημόσια ή ο σύνδεσμος δεν είναι έγκυρος."
              : "This report is no longer public or the link is invalid."}
          </p>
          <Link to="/" className="btn-primary inline-block mt-6 rounded-full px-6 py-3 font-semibold">
            {lang === "el" ? "Ανακαλύψτε το Dikigoros.io" : "Discover Dikigoros.io"}
          </Link>
        </div>
      </main>
    );
  }

  if (!report) {
    return (
      <main className="min-h-screen flex items-center justify-center text-slate-500" data-testid="public-loading">
        {t("common.loading")}
      </main>
    );
  }

  const p = report.payload || {};

  // Confidence badge colour
  const confColor = (() => {
    const c = (p.confidence || "").toLowerCase();
    if (c === "high") return "bg-emerald-100 text-emerald-700 border-emerald-200";
    if (c === "medium") return "bg-amber-100 text-amber-700 border-amber-200";
    if (c === "low") return "bg-rose-100 text-rose-700 border-rose-200";
    return "bg-slate-100 text-slate-600 border-slate-200";
  })();
  const uncertainty = Array.isArray(p.uncertainty_notes)
    ? p.uncertainty_notes.filter(Boolean)
    : (typeof p.uncertainty_notes === "string" && p.uncertainty_notes.trim() ? [p.uncertainty_notes.trim()] : []);

  return (
    <main className="min-h-screen bg-[#FAFAFA] relative" data-testid="public-report">
      {/* Diagonal watermark */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none overflow-hidden" aria-hidden="true">
        <div className="text-[#0A192F] opacity-[0.045] font-display font-bold font-bold whitespace-nowrap"
             style={{ fontSize: "20vw", transform: "rotate(-30deg)" }}>
          Dikigoros.io
        </div>
      </div>

      {/* Top brand bar */}
      <header className="relative z-10 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-brand">
            <img src="/dikigoros-logo.png" alt="Dikigoros.io" className="h-8 w-auto" width={800} height={239} />
          </Link>
          <div className="flex items-center gap-2">
            <a href={`${API_BASE}/public/reports/${token}/export?format=pdf`}
               className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-1"
               data-testid="public-download-pdf">
              <Download className="h-3.5 w-3.5" /> PDF
            </a>
            <Link to="/signup" className="btn-primary rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1"
                  data-testid="public-cta">
              <Sparkles className="h-3.5 w-3.5" /> {lang === "el" ? "Δική σας ανάλυση" : "Get your own"}
            </Link>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="kicker flex items-center gap-2"><Eye className="h-3 w-3" /> {lang === "el" ? "Δημόσια προεπισκόπηση" : "Public preview"}</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl md:text-5xl text-slate-900 tracking-tighter max-w-4xl" data-testid="public-question">{report.question}</h1>
        {report.matter_title && (
          <div className="mt-2 text-sm text-slate-500">{report.matter_title}</div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-400">
            {new Date(report.created_at).toLocaleDateString()} · {report.language?.toUpperCase()}
          </span>
          {p.confidence && (
            <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${confColor}`}
                  data-testid="public-confidence">
              {t("report.confidence")}: {p.confidence}
            </span>
          )}
        </div>

        {uncertainty.length > 0 && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 p-5" data-testid="public-uncertainty">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("report.uncertaintyNotes")}
            </div>
            <ul className="mt-2 space-y-1.5 list-disc ml-5 text-sm text-amber-900/90">
              {uncertainty.map((u, i) => <li key={i}>{String(u)}</li>)}
            </ul>
          </div>
        )}

        {/* Sidebar outline + sticky confidence pill (desktop) | content */}
        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start hidden lg:block">
            <div className="kicker mb-3">{lang === "el" ? "Πλοήγηση" : "Outline"}</div>
            <nav className="space-y-1" data-testid="public-nav">
              {ORDER.map((key, i) => {
                const active = activeSection === key;
                return (
                  <a
                    key={key}
                    href={`#public-sec-${key}`}
                    data-testid={`public-nav-${key}`}
                    className={`block text-sm transition rounded-lg px-3 py-2 flex items-center gap-2 border-l-2 ${
                      active
                        ? "bg-[#8B1E3F]/10 text-[#0A192F] font-semibold border-[#8B1E3F]"
                        : "text-slate-600 hover:text-[#0A192F] hover:bg-slate-50 border-transparent"
                    }`}
                  >
                    <span className={`font-mono text-[10px] w-6 ${active ? "text-[#8B1E3F] font-bold" : "text-slate-400"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 line-clamp-1">{t(`report.${key}`)}</span>
                  </a>
                );
              })}
            </nav>
            {p.confidence && (
              <div className={`mt-6 rounded-xl border p-4 ${confColor}`} data-testid="public-confidence-sticky">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1">{t("report.confidence")}</div>
                <div className="font-display font-extrabold text-2xl capitalize">{p.confidence}</div>
              </div>
            )}
          </aside>

          <article className="lg:col-span-9 space-y-12">
            {ORDER.map((key, i) => (
              <section key={key} id={`public-sec-${key}`} className="report-section" data-testid={`public-section-${key}`}>
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0A192F] tracking-tight">{t(`report.${key}`)}</h2>
                </div>
                <div className="mt-4">{renderSection(p[key])}</div>
              </section>
            ))}
          </article>
        </div>

        <div className="mt-16 rounded-3xl bg-[#0A192F] text-white p-10 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10"
               style={{ backgroundImage: "radial-gradient(circle at 30% 20%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="relative">
            <div className="kicker !text-[#7DD3DC]">Dikigoros.io</div>
            <h3 className="mt-2 font-display font-bold text-3xl">
              {lang === "el" ? "Δομημένη νομική ανάλυση για την Κύπρο." : "Structured legal analysis for Cyprus."}
            </h3>
            <p className="mt-2 text-blue-100 max-w-xl">
              {lang === "el"
                ? "Δημιουργήστε τη δική σας αναφορά με βάση τα δικά σας γεγονότα και έγγραφα. Δωρεάν 14ήμερη δοκιμή Professional."
                : "Create your own report from your own facts and documents. 14-day Professional trial, free."}
            </p>
            <Link to="/signup" className="mt-6 inline-flex rounded-full bg-white text-[#0A192F] px-6 py-3 font-semibold hover:bg-slate-100"
                  data-testid="public-cta-bottom">
              {lang === "el" ? "Ξεκινήστε δωρεάν" : "Start free"}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
